/**
 * Single-flight queue that serializes async tasks. Use it to wrap fetches when
 * the target server can't handle concurrency (Render free tier = 1 req at a
 * time → 503 otherwise). Order is FIFO; errors propagate without breaking the
 * queue.
 */
export class FetchQueue {
  constructor({ maxConcurrent = 1 } = {}) {
    this.maxConcurrent = maxConcurrent;
    this.active = 0;
    this.queue = [];
  }

  run(task) {
    return new Promise((resolve, reject) => {
      this.queue.push({ task, resolve, reject });
      this._next();
    });
  }

  _next() {
    if (this.active >= this.maxConcurrent) return;
    const item = this.queue.shift();
    if (!item) return;
    this.active++;
    Promise.resolve()
      .then(item.task)
      .then(
        (val) => {
          this.active--;
          item.resolve(val);
          this._next();
        },
        (err) => {
          this.active--;
          item.reject(err);
          this._next();
        }
      );
  }
}

// Module-level default queue for app-wide use against Render backend.
export const backendQueue = new FetchQueue({ maxConcurrent: 1 });

/**
 * Convenience: fetch() wrapped through the default queue.
 * Returns the raw Response; caller .json()s as usual.
 */
export function queuedFetch(url, options) {
  return backendQueue.run(() => fetch(url, options));
}
